// components/playground/sections/PLSettingsModal.jsx
import React, { useState, useEffect } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import Modal from '../../../common/Modal';
import { getButtonClass, getAlertClass, MODAL_THEME } from '../../../../config/theme.config';

export const PlaygroundSettingsModal = ({
  isOpen,
  onClose,
  accountingMethod,
  averagingMethod,
  onSave,
  currentMonth,
  isSaving = false
}) => {
  const [localAccountingMethod, setLocalAccountingMethod] = useState(accountingMethod);
  const [localAveragingMethod, setLocalAveragingMethod] = useState(averagingMethod);
  const [showAccountingInfo, setShowAccountingInfo] = useState(false);
  const [showProjectionInfo, setShowProjectionInfo] = useState(false);

  const hasUnsavedChanges = 
    localAccountingMethod !== accountingMethod || 
    localAveragingMethod !== averagingMethod;

  useEffect(() => {
    if (isOpen) {
      setLocalAccountingMethod(accountingMethod);
      setLocalAveragingMethod(averagingMethod);
    }
  }, [isOpen, accountingMethod, averagingMethod]);

  const getMonthName = (monthNum) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return months[monthNum - 1] || '';
  };

  const handleSave = () => {
    onSave({
      accountingMethod: localAccountingMethod,
      averagingMethod: localAveragingMethod
    });
  };

  const handleCancel = () => {
    setLocalAccountingMethod(accountingMethod);
    setLocalAveragingMethod(averagingMethod);
    onClose();
  };

  const footer = (
    <>
      <button
        onClick={handleCancel}
        disabled={isSaving}
        className={getButtonClass('secondary')}
      >
        Cancel
      </button>
      <button
        onClick={handleSave}
        disabled={!hasUnsavedChanges || isSaving}
        className={getButtonClass('primary')}
      >
        {isSaving ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline-block mr-2" />
            Saving...
          </>
        ) : (
          'Save Changes'
        )}
      </button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title="Scenario Settings"
      footer={footer}
      size="sm"
      closeOnBackdropClick={!isSaving}
      closeOnEscape={!isSaving}
    >
      {/* Unsaved Changes Banner */}
      {hasUnsavedChanges && (
        <div className="mb-4">
          <div className={getAlertClass('warning')}>
            <p className="font-medium text-yellow-800">
              You have unsaved changes
            </p>
            <p className="text-xs text-yellow-700 mt-1">
              Click "Save Changes" to apply and refresh data
            </p>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* Accounting Method */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className={MODAL_THEME.forms.label}>
              Accounting Basis
            </label>
            <button
              onClick={() => setShowAccountingInfo(!showAccountingInfo)}
              className="text-grey-400 hover:text-brand-500 transition-colors"
              title="Learn more"
            >
              <InformationCircleIcon className="w-5 h-5" />
            </button>
          </div>
          
          {showAccountingInfo && (
            <div className={`${getAlertClass('info')} mb-3`}>
              <p className="font-medium text-brightblue-900 mb-1">Accounting Basis:</p>
              <p className="mb-2"><strong>Accrual:</strong> Revenue is recognized when earned.</p>
              <p><strong>Cash:</strong> Revenue is recognized when payment is received.</p>
            </div>
          )}

          <select
            value={localAccountingMethod}
            onChange={(e) => setLocalAccountingMethod(e.target.value)}
            disabled={isSaving}
            className={MODAL_THEME.forms.select}
          >
            <option value="Accrual">Accrual</option>
            <option value="Cash">Cash</option>
          </select>
        </div>

        {/* Forecast Method */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className={MODAL_THEME.forms.label}>
              Forecast Method (Rolling Average)
            </label>
            <button
              onClick={() => setShowProjectionInfo(!showProjectionInfo)}
              className="text-grey-400 hover:text-brand-500 transition-colors"
              title="Learn more"
            >
              <InformationCircleIcon className="w-5 h-5" />
            </button>
          </div>
          
          {showProjectionInfo && (
            <div className={`${getAlertClass('info')} mb-3`}>
              <p className="font-medium text-brightblue-900 mb-1">Rolling Average Methods:</p>
              <p className="mb-2"><strong>Last 3 Months:</strong> Most responsive to recent trends.</p>
              <p className="mb-2"><strong>Last 6 Months:</strong> Balanced approach.</p>
              <p><strong>Last 12 Months:</strong> Most stable, accounts for seasonality.</p>
            </div>
          )}

          <select
            value={localAveragingMethod}
            onChange={(e) => setLocalAveragingMethod(e.target.value)}
            disabled={isSaving}
            className={MODAL_THEME.forms.select}
          >
            <option value="last3">Last 3 Months</option>
            <option value="last6">Last 6 Months</option>
            <option value="last12">Last 12 Months</option>
          </select>
        </div>
      </div>
    </Modal>
  );
};